import React from 'react';
import { ConfirmActionDialog } from '../Components/ConfirmActionDialog';

interface ConfirmActionOptions {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  isDestructive?: boolean;
  requireText?: string;
  requireTextLabel?: string;
  onConfirm: () => Promise<void> | void;
}

export const useConfirmAction = () => {
  const [options, setOptions] = React.useState<ConfirmActionOptions | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [typedText, setTypedText] = React.useState('');

  const openConfirm = React.useCallback((next: ConfirmActionOptions) => {
    setOptions(next);
    setErrorMessage(null);
    setTypedText('');
    setIsOpen(true);
  }, []);

  const closeConfirm = React.useCallback(() => {
    if (isLoading) {
      return;
    }
    setIsOpen(false);
  }, [isLoading]);

  const handleConfirm = React.useCallback(async () => {
    if (!options) {
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage(null);
      await options.onConfirm();
      setIsOpen(false);
    } catch (error: any) {
      setErrorMessage(error?.message || 'Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  const dialog = options
    ? React.createElement(ConfirmActionDialog, {
        isOpen,
        title: options.title,
        body: options.body,
        confirmLabel: options.confirmLabel,
        isDestructive: options.isDestructive,
        isLoading,
        errorMessage,
        requireText: options.requireText,
        requireTextLabel: options.requireTextLabel,
        typedText,
        onTypedTextChange: setTypedText,
        onConfirm: handleConfirm,
        onCancel: closeConfirm,
      })
    : null;

  return { openConfirm, dialog, isOpen };
};
